import { defineAbility } from '@casl/ability';
import {
  AUDIT_TRAILS_PERMISSIONS,
  AuditTrailsPermissions,
  SIM_INVENOTRY_PERMISSIONS,
  SimInventoryPermissions,
} from './Permissions.model';
import {
  BULK_PROVISIONING_PERMISSIONS,
  BulkProvisioningPermissions,
} from 'permissions/Permissions.model';

export const Actions = {
  read: 'read',
  edit: 'edit',
  export: 'export',
  upload: 'upload',
  simulate: 'simulate',
  restore: 'restore',
  filter: 'filter',
  activation: 'activation',
  make: 'make',
} as const;

export const Subjects = {
  bulkProvisioning: 'bulkProvisioning',
  simsProfiles: 'simsProfiles',
  simDetails: 'simDetails',
  simProfilesTableConfiguration: 'simsProfilesTableConfiguration',
  tags: 'tags',
  poolingLogs: 'poolingLogs',
  outage: 'outage',
  otaConfiguration: 'otaConfiguration',
  customFields: 'customFields',
  internalData: 'internalData',
  auditTrails: 'auditTrails',
  importRsims: 'importRsims',
  rSimProducts: 'rSimProducts',
  simProducts: 'simProducts',
  tariffSummary: 'tariffSummary',
  inLifeActivation: 'inLifeActivation',
  inLifeSuspension: 'inLifeSuspension',
  inLifeUnsuspension: 'inLifeUnsuspension',
  inLifeCancelLocation: 'inLifeCancelLocation',
  inLifeTermination: 'inLifeTermination',
  productsManagement: 'productsManagement',
  subscriptionStatusReconciliation: 'subscriptionStatusReconciliation',
} as const;

export const createSimInventoryPermissionsFromRules = (
  permissions: SimInventoryPermissions[],
  can: any,
) => {
  permissions.forEach((permission) => {
    if (permission === SIM_INVENOTRY_PERMISSIONS.InLifeActivation) {
      can(Actions.make, Subjects.inLifeActivation);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.InLifeSuspension) {
      can(Actions.make, Subjects.inLifeSuspension);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.InLifeUnsuspension) {
      can(Actions.make, Subjects.inLifeUnsuspension);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.InLifeCancelLocation) {
      can(Actions.make, Subjects.inLifeCancelLocation);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.InLifeTermination) {
      can(Actions.make, Subjects.inLifeTermination);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.ViewSimListAndDetails) {
      can(Actions.read, Subjects.simsProfiles);
      can(Actions.read, Subjects.simDetails);
      can(Actions.edit, Subjects.simProfilesTableConfiguration);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.Export) {
      can(Actions.export, Subjects.simsProfiles);
      can(Actions.export, Subjects.rSimProducts);
    }
    if (permission === SIM_INVENOTRY_PERMISSIONS.EditTags) {
      can(Actions.edit, Subjects.tags);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.ExportPoolingLogs) {
      can(Actions.export, Subjects.poolingLogs);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.RestoreOTAConfiguration) {
      can(Actions.restore, Subjects.otaConfiguration);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.ViewOtaConfigurationsDetails) {
      can(Actions.read, Subjects.otaConfiguration);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.SimulateOutage) {
      can(Actions.simulate, Subjects.outage);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.ViewCustomFields) {
      can(Actions.read, Subjects.customFields);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.EditCustomFields) {
      can(Actions.edit, Subjects.customFields);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.HasInternalDataAccess) {
      can(Actions.filter, Subjects.internalData);
      can(Actions.read, Subjects.internalData);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.ImportRsims) {
      can(Actions.edit, Subjects.importRsims);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.RSimProductProvisioning) {
      can(Actions.activation, Subjects.rSimProducts);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.SimProductProvisioning) {
      can(Actions.activation, Subjects.simProducts);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.AggregatedUsageView) {
      can(Actions.read, Subjects.tariffSummary);
    }

    if (permission === SIM_INVENOTRY_PERMISSIONS.SubscriptionStatusReconciliation) {
      can(Actions.upload, Subjects.subscriptionStatusReconciliation);
    }
  });
};

export const createBulkProvisioningPermissionsFromRules = (
  permissions: BulkProvisioningPermissions[],
  can: any,
) => {
  permissions.forEach((permission) => {
    if (permission === BULK_PROVISIONING_PERMISSIONS.UploadFile) {
      can(Actions.upload, Subjects.bulkProvisioning);
    }

    if (permission === BULK_PROVISIONING_PERMISSIONS.ViewTableAndDetails) {
      can(Actions.read, Subjects.bulkProvisioning);
    }

    if (permission === BULK_PROVISIONING_PERMISSIONS.DownloadReport) {
      can(Actions.export, Subjects.bulkProvisioning);
    }

    if (permission === BULK_PROVISIONING_PERMISSIONS.ProductManagementView) {
      can(Actions.read, Subjects.productsManagement);
    }
  });
};

export const createAuditTrailsPermissionsFromRules = (
  permissions: AuditTrailsPermissions[],
  can: any,
) => {
  permissions.forEach((permission) => {
    if (permission === AUDIT_TRAILS_PERMISSIONS.export) {
      can(Actions.export, Subjects.auditTrails);
    }

    if (permission === AUDIT_TRAILS_PERMISSIONS.viewAuditTrailList) {
      can(Actions.read, Subjects.auditTrails);
    }
  });
};

export const ability = defineAbility(() => {});

export const defineBulkProvisioningUserAbility = (_can: any, _cannot: any) => {};

export const defineSimInventoryUserAbility = (can: any, cannot: any) => {
  can(Actions.read, Subjects.simsProfiles);
  can(Actions.read, Subjects.simDetails);
  can(Actions.edit, Subjects.simProfilesTableConfiguration);
  can(Actions.export, Subjects.simsProfiles);
  can(Actions.edit, Subjects.tags);
  can(Actions.export, Subjects.poolingLogs);
  can(Actions.simulate, Subjects.outage);

  cannot(Actions.restore, Subjects.otaConfiguration);
  cannot(Actions.read, Subjects.bulkProvisioning);
  cannot(Actions.read, Subjects.customFields);
  cannot(Actions.filter, Subjects.internalData);
};

export const defineSimInventoryAdminAbility = (can: any) => {
  can(Actions.read, Subjects.simsProfiles);
  can(Actions.read, Subjects.simDetails);
  can(Actions.read, Subjects.otaConfiguration);
  can(Actions.edit, Subjects.simProfilesTableConfiguration);
  can(Actions.export, Subjects.simsProfiles);
  can(Actions.export, Subjects.poolingLogs);
  can(Actions.edit, Subjects.tags);

  can(Actions.restore, Subjects.otaConfiguration);
  can(Actions.simulate, Subjects.outage);
  can(Actions.read, Subjects.customFields);
  can(Actions.edit, Subjects.customFields);
  can(Actions.filter, Subjects.internalData);
  can(Actions.read, Subjects.internalData);

  can(Actions.upload, Subjects.bulkProvisioning);
  can(Actions.read, Subjects.bulkProvisioning);
  can(Actions.export, Subjects.bulkProvisioning);

  can(Actions.export, Subjects.auditTrails);
  can(Actions.read, Subjects.auditTrails);

  can(Actions.activation, Subjects.rSimProducts);
  can(Actions.activation, Subjects.simProducts);

  can(Actions.read, Subjects.productsManagement);
  can(Actions.upload, Subjects.subscriptionStatusReconciliation);
};

export const defineAdminAbility = () => defineAbility(defineSimInventoryAdminAbility);
export const defineUserAbility = () => defineAbility(defineSimInventoryUserAbility);
